import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import { 
  FiBook, 
  FiDownload, 
  FiClock, 
  FiCalendar,
  FiFile,
  FiCheckCircle,
  FiXCircle,
  FiChevronDown,
  FiChevronUp
} from "react-icons/fi";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import PageTitle from "../../components/PageTitle";
import "./ClassroomView.css";
import { staffRequest } from "../../helpers/uri";

const ClassroomView = () => {
  const { id } = useParams();
  const [expandedTopics, setExpandedTopics] = useState({});

  const { data, error, isLoading } = useQuery({
    queryKey: ["classTopic", id],
    queryFn: async () => {
      const req = await axiosget(`ms_course_topic/${id}`);
      return req?.data;
    },
    enabled: !!id,
    onSuccess: (data) => {
      if (data?.topic) {
        // Initialize state: first topic expanded, others collapsed
        const initialExpanded = {};
        data.topic.forEach((topic, index) => {
          initialExpanded[topic._id || index] = index === 0;
        });
        setExpandedTopics(initialExpanded);
      }
    }
  });

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.includes('pdf')) return <FiFile className="file-icon pdf" />;
    if (mimeType.includes('word')) return <FiFile className="file-icon word" />;
    if (mimeType.includes('powerpoint')) return <FiFile className="file-icon powerpoint" />;
    return <FiFile className="file-icon generic" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="classroom-container">
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error.message}`} />}
      {data?.type !== undefined && <ErrorResponse type="danger" message={data?.message} />}
      {data?.type === undefined && data?.length === 0 && (
        <ErrorResponse 
          type="info" 
          title="No Topics Found" 
          message="There are currently no topics available for this course" 
        />
      )}

      {data?.type === undefined && data?.topic && (
        <>
          <PageTitle 
            title={data.course?.code} 
            sub={`Course materials for ${data.course?.name}`} 
            icon={<FiBook />}
          />

          <div className="topics-list">
            {data.topic.map((topic, index) => (
              <div key={`topic-${topic._id || index}`} className="topic-card">
                <div 
                  className="topic-header"
                  onClick={() => toggleTopic(topic._id || index)}
                >
                  <div className="topic-header-content">
                    <h2 className="topic-title">{topic.label}</h2>
                    <div className={`topic-status ${topic.status ? 'active' : 'inactive'}`}>
                      {topic.status ? (
                        <>
                          <FiCheckCircle className="status-icon" />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <FiXCircle className="status-icon" />
                          <span>Inactive</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button className="toggle-button">
                    {expandedTopics[topic._id || index] ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                </div>

                {expandedTopics[topic._id || index] && (
                  <>
                    <div className="topic-meta">
                      <div className="meta-item">
                        <FiCalendar className="meta-icon" />
                        <span>Created: {formatDate(topic.createdAt)}</span>
                      </div>
                      <div className="meta-item">
                        <FiClock className="meta-icon" />
                        <span>Updated: {formatDate(topic.updatedAt)}</span>
                      </div>
                    </div>

                    {topic.materials?.length > 0 && (
                      <div className="materials-section">
                        {topic.materials.map((material, matIndex) => (
                          <div key={`material-${matIndex}`} className="material-card">
                            <h3 className="material-title">{material.label}</h3>
                            
                            <div className="material-status">
                              <span className={`status-badge ${material.status ? 'active' : 'inactive'}`}>
                                {material.status ? 'Active' : 'Inactive'}
                              </span>
                            </div>

                            {material.description && (
                              <div className="material-description">
                                <p>{material.description}</p>
                              </div>
                            )}

                            {material.files?.length > 0 && (
                              <>
                                <h4 className="files-title">Attached Files</h4>
                                <div className="files-grid">
                                  {material.files.map((file) => (
                                    <div key={file._id} className="file-card">
                                      <div className="file-header">
                                        {getFileIcon(file.mimetype)}
                                        <div className="file-info">
                                          <h5 className="file-name">{file.originalname}</h5>
                                          <span className="file-meta">
                                            {file.mimetype.split('/')[1]} • {formatFileSize(file.size)}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="file-footer">
                                        <span className="upload-date">
                                          Uploaded: {formatDate(file.uploadDate)}
                                        </span>
                                        <a
                                          href={`${staffRequest}ms_course_materials/download/${file._id}`}
                                          className="download-btn"
                                          download={file.originalname}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <FiDownload className="download-icon" />
                                          Download
                                        </a>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ClassroomView;